/**
 * NotebookHome — the workspace notebook's front page (`/w/:wsId/n`).
 *
 * The sidebar tree is the primary way into a page. This screen is the
 * "everything in this workspace" overview that the 📓 icon in the sidebar
 * opens (FRIDAY-107): every page of the workspace, in tree order, with the
 * path of its parents, filterable by title.
 *
 * Archived pages are not listed — they live in Workspace settings → Pages.
 */

import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { usePageTree } from '@/features/notebook/pages'
import { buildTree, findPath, flattenTree } from '@/features/notebook/tree'
import { CreatePageDialog } from './CreatePageDialog'
import type { Page } from '@friday/shared/realtime'

export interface NotebookHomeProps {
  wsId: string
}

interface PageRow {
  page: Page
  /** Titles of the page's ancestors, root first. */
  path: string[]
}

export function NotebookHome({ wsId }: NotebookHomeProps) {
  const [query, setQuery] = useState('')
  const [creating, setCreating] = useState(false)
  const tree = usePageTree(wsId)
  const navigate = useNavigate()

  const pages = tree.data ?? []

  const rows = useMemo<PageRow[]>(() => {
    const flat = flattenTree(buildTree(pages)).filter(({ page }) => !page.archivedAt)
    return flat.map(({ page }) => ({
      page,
      path: findPath(pages, page.id)
        .slice(0, -1)
        .map((p) => p.title),
    }))
  }, [pages])

  const needle = query.trim().toLowerCase()
  const visible = useMemo(() => {
    if (!needle) return rows
    return rows.filter(
      (row) =>
        row.page.title.toLowerCase().includes(needle) ||
        row.path.some((title) => title.toLowerCase().includes(needle)),
    )
  }, [rows, needle])

  return (
    <main className="bg-app flex min-w-0 flex-1 overflow-auto">
      <div className="mx-auto w-full max-w-3xl px-6 py-10">
        <header className="mb-6">
          <div className="mb-3 text-4xl">📓</div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold">Notebook</h1>
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              New page
            </button>
          </div>
          <p className="text-surface-500 mt-1 text-sm">
            {rows.length === 0
              ? 'Every page in this workspace shows up here.'
              : `${rows.length} ${rows.length === 1 ? 'page' : 'pages'} in this workspace.`}
          </p>
        </header>

        {rows.length > 0 && (
          <div className="mb-4">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="search"
              placeholder="Search pages…"
              aria-label="Search pages"
              className="input max-w-sm"
            />
          </div>
        )}

        {tree.isLoading && <div className="text-surface-500 text-sm">Loading pages…</div>}

        {!tree.isLoading && rows.length === 0 && (
          <div className="card p-6 text-center">
            <h2 className="mb-1 text-base font-semibold">No pages yet</h2>
            <p className="text-surface-500 mb-4 text-sm">
              Pages are notes that live next to your boards. Create the first one.
            </p>
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              Create a page
            </button>
          </div>
        )}

        {!tree.isLoading && rows.length > 0 && visible.length === 0 && (
          <div className="text-surface-500 py-8 text-center text-sm">No page matches “{query.trim()}”.</div>
        )}

        {visible.length > 0 && (
          <ul className="divide-surface-200 dark:divide-surface-800 divide-y">
            {visible.map(({ page, path }) => (
              <li key={page.id}>
                <Link
                  to="/w/$wsId/n/$pageId"
                  params={{ wsId, pageId: page.id }}
                  className="hover:bg-surface-100 dark:hover:bg-surface-800 flex items-center gap-3 rounded-md px-2 py-2.5"
                >
                  <span aria-hidden="true" className="text-lg leading-none">
                    {page.icon ?? '📄'}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{page.title}</span>
                    {path.length > 0 && (
                      <span className="text-surface-500 block truncate text-xs">{path.join(' / ')}</span>
                    )}
                  </span>
                  <span className="text-surface-500 shrink-0 text-xs">
                    <span aria-hidden="true">{formatRelative(page.updatedAt)}</span>
                    <span className="sr-only">updated {formatRelative(page.updatedAt)}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {creating && (
        <CreatePageDialog
          wsId={wsId}
          defaultParentId={null}
          onClose={() => setCreating(false)}
          onCreated={(pageId) => navigate({ to: '/w/$wsId/n/$pageId', params: { wsId, pageId } })}
        />
      )}
    </main>
  )
}

/** "3m ago" / "2d ago" — the same wording the archive list uses. */
function formatRelative(iso: string | null | undefined): string {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 0) return 'just now'
  const min = Math.floor(ms / 60_000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const d = Math.floor(hr / 24)
  if (d < 30) return `${d}d ago`
  return new Date(iso).toLocaleDateString()
}
